import React, { useCallback, useMemo, useState, useEffect } from "react";
import { ApolloError } from "@apollo/client";
import { useParams, useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import SendIcon from "@mui/icons-material/Send";
import CircularProgress from "@mui/material/CircularProgress";
import { useAuth } from "../../context/AuthContext";
import { useGetUserQuery } from "../../graphql/queries/getUser.generated";
import {
  useAddAiChatMessageMutation,
  AddAiChatMessageMutationVariables,
} from "../../graphql/mutations/addWebAiChatMessage.generated";
import { WebAiChatMessageOutput } from "../../graphql/generated/graphql";
import { useGetWebAiChatMessagesQuery } from "../../graphql/queries/getWebAiChatMessages.generated";
import routeNames from "../../constants/route-names";
import styles from "./ai-chat-conversation.style";
import { Typography } from "@mui/material";
import Markdown from "react-markdown";

const AiChatConversation = () => {
  const { loggedUser } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const initialChatId = params.id ? (params.id === "new" ? "" : params.id) : "";
  const [userMessage, setUserMessage] = useState<string>("");
  const [userMessageError, setUserMessageError] = useState<string>("");
  const [chatId, setChatId] = useState<string>(initialChatId);
  const [chatMessages, setChatMessages] = useState<
    Array<WebAiChatMessageOutput>
  >([]);

  const preprocessMarkdown = useCallback((messageBody: string): string => {
    if (!messageBody) return "";

    if (messageBody[0] === "`") {
      return messageBody
        .replace(/```markdown\s*/g, "")
        .replace(/```$/g, "")
        .trim();
    } else {
      return messageBody;
    }
  }, []);

  const handleOnErrorsGetWebAiChatMessages = useCallback(
    (error: ApolloError) => {
      console.log("GetWebAiChatMessages Error: ", error);
    },
    []
  );

  const handleOnCompletedGetWebAiChatMessages = useCallback((data: any) => {
    if (data.getWebAiChatMessages && data.getWebAiChatMessages.length) {
      setChatMessages(data.getWebAiChatMessages);
    }
  }, []);

  const { data: chatMessagesData, loading: loadingChatMessages } =
    useGetWebAiChatMessagesQuery({
      skip: !loggedUser?.uid || chatId === "",
      variables: {
        aiChatId: chatId,
      },
      errorPolicy: "none",
      fetchPolicy: "network-only",
      nextFetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      onError: handleOnErrorsGetWebAiChatMessages,
      onCompleted: handleOnCompletedGetWebAiChatMessages,
    });

  const handleOnErrorsGetUsers = useCallback((error: ApolloError) => {
    console.log("Get Users Error: ", error);
  }, []);

  const { data, loading, refetch } = useGetUserQuery({
    skip: !loggedUser?.uid,
    variables: {
      id: loggedUser?.uid,
    },
    errorPolicy: "none",
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorsGetUsers,
  });

  const handleOnErrorAddAiChatMessage = useCallback((error: ApolloError) => {
    console.log("Add Ai Chat Message Error: ", error);
  }, []);

  const handleOnCompletedAddAiChatMessage = useCallback(
    (data: any) => {
      if (data.newWebAiChatMessage && data.newWebAiChatMessage.length) {
        setChatId(data.newWebAiChatMessage[0].chatId);
        setChatMessages(data.newWebAiChatMessage);

        if (params.id !== data.newWebAiChatMessage[0].chatId) {
          navigate(
            `${routeNames.AI_CHATS}/${data.newWebAiChatMessage[0].chatId}`
          );
        }
      }
    },
    [navigate, params]
  );

  const [addAiChatMessageMutation, { loading: loadingAddAiChatMessage }] =
    useAddAiChatMessageMutation({
      errorPolicy: "none",
      notifyOnNetworkStatusChange: true,
      onCompleted: handleOnCompletedAddAiChatMessage,
      onError: handleOnErrorAddAiChatMessage,
    });

  const handleOnPressSend = useCallback(() => {
    if (userMessage && data?.users_by_pk) {
      const newMessagesList = chatMessages.concat([
        {
          id: "ramdom-ID",
          isSystem: false,
          message: userMessage,
          user: null,
          createdAt: "",
          chatId: "",
        },
      ]);

      setChatMessages(newMessagesList);
      setUserMessage("");

      const mutationVariables: AddAiChatMessageMutationVariables = {
        message: userMessage,
        userId: data?.users_by_pk?.id,
        workspaceId: data?.users_by_pk?.workspaces[0]?.workspaceId,
      };

      if (chatId) {
        mutationVariables.aiChatId = chatId;
      }
      addAiChatMessageMutation({
        variables: mutationVariables,
      });
    }
  }, [userMessage, data, chatId, chatMessages]);

  const chatMessagesElements = useMemo(() => {
    return (
      <Box sx={styles.conversationContainer}>
        {chatMessages.map((element) => {
          const {
            id,
            chatId,
            createdAt,
            message,
            isSystem,
            user,
            userId,
            webAiChat,
          } = element;

          if (isSystem) {
            return (
              <Box
                key={`chat-message-${id}`}
                sx={styles.systemMessagecontainer}
              >
                <Avatar
                  alt="HiGnome"
                  src={
                    process.env.PUBLIC_URL + "/assets/images/hignome-avatar.svg"
                  }
                  sx={styles.hignomeAvatar}
                />
                <Paper square={false} elevation={3} sx={styles.systemMessage}>
                  <Markdown>{preprocessMarkdown(message.trimStart())}</Markdown>
                </Paper>
              </Box>
            );
          } else {
            const { firstName, lastName, profilePhotoLink } =
              user || data?.users_by_pk || {};

            return (
              <Box key={`chat-message-${id}`} sx={styles.userMessageContainer}>
                <Paper square={false} elevation={3} sx={styles.userMessage}>
                  <Markdown>{preprocessMarkdown(message.trimStart())}</Markdown>
                </Paper>
                <Avatar
                  alt={`${firstName} ${lastName}`}
                  src={profilePhotoLink || undefined}
                />
              </Box>
            );
          }
        })}
        {loading || loadingAddAiChatMessage || loadingChatMessages ? (
          <Box sx={styles.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : null}
      </Box>
    );
  }, [
    chatMessages,
    data?.users_by_pk,
    loading,
    loadingAddAiChatMessage,
    loadingChatMessages,
  ]);

  return (
    <Container maxWidth={false} sx={styles.container}>
      <Box sx={{ textAlign: "center", marginBottom: 2 }}>
        <Typography
          variant="body1"
          component="p"
          sx={{
            fontSize: "16px",
            fontFamily: "Arial, sans-serif",
            color: "black",
          }}
        >
          Here, you can chat with your Highnote data, including user
          conversations and any knowledge (policies, etc.) that has been added
          to your organization's workspace.
        </Typography>
      </Box>
      {chatMessagesElements}
      <Paper component="form" sx={styles.inputContainer}>
        <TextField
          variant="filled"
          id="outlined-multiline-static"
          label={chatId ? "Ask a follow up..." : "Ask your question here..."}
          fullWidth
          required={true}
          error={!!userMessageError}
          helperText={userMessageError}
          value={userMessage}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setUserMessage(event.target.value);
            setUserMessage(event.target.value);
            if (event.target.value && userMessageError) {
              setUserMessageError("");
            }
          }}
          onBlur={() => {
            if (userMessage.length) {
              setUserMessageError("");
            } else {
              setUserMessageError("The message is required.");
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.keyCode === 13) {
              handleOnPressSend();
            }
          }}
        />
        <Divider sx={styles.inputDivider} orientation="vertical" />
        <IconButton
          color="primary"
          size="large"
          sx={{ p: "10px" }}
          onClick={handleOnPressSend}
        >
          <SendIcon />
        </IconButton>
      </Paper>
    </Container>
  );
};

export default AiChatConversation;
